/**
 * StudentProfileTab.jsx
 * Profil formunu gosterir ve PUT /admin/students/{id}/profile ile gunceller.
 *
 * NOT: Rol degisimi bu formda YAPILMAZ.
 * Rol degisimi icin AdminStudentHub ActionMenu -> "Change Role" akisini kullanin
 * (PUT /admin/users/{id}/role endpoint'i).
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { getApiErrorMessage } from '../../utils/apiError';
import { motion } from 'framer-motion';
import { Save, CheckCircle, XCircle } from 'lucide-react';
import { StatusBadge, ConfirmDialog } from '../../components/admin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CABIN_LEVELS = ['', 'beginner', 'intermediate', 'advanced'];

/**
 * TeyitBadge — Alan teyit rozetini gösterir, tıklayınca toggle diyaloğu açar.
 */
function TeyitBadge({ userId, alan, labelKey: _labelKey, teyitler, onSuccess }) {
  const { t, lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const current = teyitler?.[alan] === true;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API}/admin/students/${userId}/teyit`,
        { alan, deger: !current },
        { headers: authHeaders() }
      );
      toast.success(
        current ? t('admin.studentHub.teyit_edilmedi') : t('admin.studentHub.teyit_edildi')
      );
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setConfirmOpen(true)}
        className="flex items-center gap-1 focus:outline-none group"
        title={t('admin.studentHub.teyit_confirm_desc')}
        data-testid={`teyit-badge-${alan}`}
      >
        {current ? (
          <CheckCircle size={14} className="text-emerald-500 group-hover:text-emerald-700 transition-colors" />
        ) : (
          <XCircle size={14} className="text-rose-500 group-hover:text-rose-700 transition-colors" />
        )}
        <span
          className={`text-[10px] font-bold ${
            current ? 'text-emerald-600' : 'text-rose-600'
          }`}
        >
          {current ? t('admin.studentHub.teyit_edildi') : t('admin.studentHub.teyit_edilmedi')}
        </span>
      </button>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(v) => !loading && setConfirmOpen(v)}
        title={t('admin.studentHub.teyit_confirm_title')}
        description={`${t(`admin.studentHub.teyit_${alan}`)} — ${
          current ? t('admin.studentHub.teyit_edildi') : t('admin.studentHub.teyit_edilmedi')
        } → ${current ? t('admin.studentHub.teyit_edilmedi') : t('admin.studentHub.teyit_edildi')}`}
        variant={current ? 'warning' : 'info'}
        confirmLabel={lang === 'tr' ? 'Güncelle' : 'Update'}
        onConfirm={handleConfirm}
        loading={loading}
      />
    </>
  );
}

export default function StudentProfileTab({ detail, updateStudent, onSuccess }) {
  const { t, lang } = useLanguage();
  const toast = useToast();

  const user = detail?.user || {};

  /**
   * Build full form state from user document.
   * All known profile fields are included so the PUT payload always
   * contains the complete record — even if the user only edits one field.
   *
   * KEY NAMING: backend allowlist uses "tc_no" — never "tc_kimlik".
   * role is intentionally excluded; use PUT /admin/users/{id}/role instead.
   */
  const buildFormState = (u) => ({
    name: u.name || '',
    email: u.email || '',
    phone: u.phone || '',
    cabin_crew_level: u.cabin_crew_level || '',
    address: u.address || '',
    city: u.city || '',
    district: u.district || '',
    postal_code: u.postal_code || '',
    // tc_no matches backend allowlist; tc_kimlik is a legacy alias — fall back to it
    tc_no: u.tc_no || u.tc_kimlik || '',
    birth_date: u.birth_date || '',
    emergency_contact: u.emergency_contact || '',
    emergency_phone: u.emergency_phone || '',
    notes: u.notes || '',
    // role is NOT included — use dedicated /role endpoint
  });

  const [form, setForm] = useState(() => buildFormState(user));

  // Sync when a different user is selected
  useEffect(() => {
    setForm(buildFormState(user));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.user_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Send only fields with a meaningful value so we never overwrite
      // existing data with empty strings coming from uninitialised fields.
      // If the user explicitly cleared a field we still send it (empty string
      // is intentional); null/undefined are the only values we drop.
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== null && v !== undefined)
      );
      await updateStudent.mutateAsync(payload);
      toast.success(t('admin.studentHub.profile_saved'));
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const field = (key, labelKey, type = 'text', children) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-bold text-[var(--text-muted)]">{t(`admin.studentHub.${labelKey}`)}</label>
      {children || (
        <input
          type={type}
          value={form[key] || ''}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] focus:border-[var(--brand-primary)] focus:outline-none text-sm font-semibold"
        />
      )}
    </div>
  );

  const userId = user.user_id;
  const teyitler = user.teyitler || {};

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* ── Durum rozetleri ─────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2 pb-2 border-b border-[var(--border-light)]">
        <StatusBadge
          variant={user.pasif ? 'danger' : 'success'}
          label={user.pasif ? t('admin.studentHub.badge_inactive') : t('admin.studentHub.badge_active')}
          dot
        />
        {user.dogrulandi && (
          <StatusBadge variant="info" label={t('admin.studentHub.badge_verified')} />
        )}
        {typeof user.ek_ders_hakki === 'number' && user.ek_ders_hakki > 0 && (
          <StatusBadge
            variant="warning"
            label={t('admin.studentHub.badge_ek_ders').replace('{{n}}', user.ek_ders_hakki)}
          />
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {field('name', 'profile_name')}
        {field('email', 'profile_email', 'email')}
        {field('phone', 'profile_phone', 'tel')}
        {field('city', 'profile_city')}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {field('district', 'profile_district')}
        {field('postal_code', 'profile_postal_code')}
      </div>

      {/* Adres — teyit rozetiyle */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-[var(--text-muted)]">
            {t('admin.studentHub.profile_address')}
          </label>
          {userId && (
            <TeyitBadge
              userId={userId}
              alan="adres"
              labelKey="teyit_adres"
              teyitler={teyitler}
              onSuccess={onSuccess}
            />
          )}
        </div>
        <input
          type="text"
          value={form.address || ''}
          onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
          className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] focus:border-[var(--brand-primary)] focus:outline-none text-sm font-semibold"
        />
      </div>

      {/* TC No + Doğum Tarihi — teyit rozetleriyle */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold text-[var(--text-muted)]">
              {t('admin.studentHub.profile_tc_no')}
            </label>
            {userId && (
              <TeyitBadge
                userId={userId}
                alan="tc"
                labelKey="teyit_tc"
                teyitler={teyitler}
                onSuccess={onSuccess}
              />
            )}
          </div>
          <input
            type="text"
            value={form.tc_no || ''}
            onChange={(e) => setForm((f) => ({ ...f, tc_no: e.target.value }))}
            className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] focus:border-[var(--brand-primary)] focus:outline-none text-sm font-semibold"
          />
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold text-[var(--text-muted)]">
              {t('admin.studentHub.profile_birth_date')}
            </label>
            {userId && (
              <TeyitBadge
                userId={userId}
                alan="dogum_tarihi"
                labelKey="teyit_dogum_tarihi"
                teyitler={teyitler}
                onSuccess={onSuccess}
              />
            )}
          </div>
          <input
            type="date"
            value={form.birth_date || ''}
            onChange={(e) => setForm((f) => ({ ...f, birth_date: e.target.value }))}
            className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] focus:border-[var(--brand-primary)] focus:outline-none text-sm font-semibold"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {field('emergency_contact', 'profile_emergency_contact')}
        {field('emergency_phone', 'profile_emergency_phone', 'tel')}
      </div>

      {field('cabin_crew_level', 'profile_cabin_level', 'text',
        <select
          value={form.cabin_crew_level || ''}
          onChange={(e) => setForm((f) => ({ ...f, cabin_crew_level: e.target.value }))}
          className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] focus:border-[var(--brand-primary)] focus:outline-none text-sm font-semibold bg-white"
        >
          {CABIN_LEVELS.map((lv) => (
            <option key={lv} value={lv}>
              {lv ? (lang === 'tr'
                ? { beginner: 'Başlangıç', intermediate: 'Orta', advanced: 'İleri' }[lv] || lv
                : lv.charAt(0).toUpperCase() + lv.slice(1))
                : (lang === 'tr' ? 'Seçin...' : 'Select...')}
            </option>
          ))}
        </select>
      )}

      {field('notes', 'profile_notes', 'text',
        <textarea
          value={form.notes || ''}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          rows={3}
          className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] focus:border-[var(--brand-primary)] focus:outline-none text-sm font-semibold resize-none"
        />
      )}

      <div className="pt-2">
        <motion.button
          type="submit"
          disabled={updateStudent.isPending}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          className="btn-primary flex items-center gap-2 px-5 py-2.5 text-sm disabled:opacity-40"
          data-testid="profile-save-btn"
        >
          <Save size={16} />
          {updateStudent.isPending
            ? t('admin.studentHub.profile_saving')
            : t('admin.studentHub.profile_save')}
        </motion.button>
      </div>
    </form>
  );
}
